"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  Sparkles,
  Zap,
  Wrench,
  Megaphone,
  Columns,
  Percent,
  Smartphone,
  MessageCircle,
  EyeOff,
  Plus,
  Share2,
  Layers,
  GraduationCap,
  Headphones,
  SlidersHorizontal,
  RefreshCw,
  Save,
  Link2,
  BarChart3,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useChatShell } from "@/components/chat/chat-shell";
import { cn } from "@/lib/utils";
import { phCapture } from "@/utils/analytics";
import {
  WHATS_NEW_UPDATES,
  MAX_UPDATES_SHOWN,
  formatUpdateDate,
  hasUnreadUpdate,
  visibleUpdates,
  type UpdateType,
  type WhatsNewUpdate,
} from "@/lib/whats-new-data";
import { markWhatsNewSeen } from "@/lib/actions/whats-new";

interface WhatsNewProps {
  lastSeenId: string | null;
  onSeen: (updateId: string) => void;
  suppressAutoOpen?: boolean;
}

const TYPE_ICONS: Record<UpdateType, React.ElementType> = {
  feature: Sparkles,
  improvement: Zap,
  fix: Wrench,
  announcement: Megaphone,
};

const ICON_MAP: Record<string, React.ElementType> = {
  Columns,
  Percent,
  Smartphone,
  MessageCircle,
  EyeOff,
  Plus,
  Share2,
  Layers,
  GraduationCap,
  Headphones,
  SlidersHorizontal,
  RefreshCw,
  Save,
  Link2,
  BarChart3,
};

const isEmojiIcon = (icon: string): boolean => icon.startsWith("emoji:");

function updateIcon(update: WhatsNewUpdate): React.ElementType {
  if (update.icon && !isEmojiIcon(update.icon)) {
    return ICON_MAP[update.icon] ?? TYPE_ICONS[update.type];
  }
  return TYPE_ICONS[update.type];
}

export const OPEN_WHATS_NEW_EVENT = "upsider:open-whats-new";

export function openWhatsNew() {
  window.dispatchEvent(new CustomEvent(OPEN_WHATS_NEW_EVENT));
}

export function useWhatsNew(lastSeenId: string | null) {
  const updates = visibleUpdates(WHATS_NEW_UPDATES, useChatShell() !== null);
  return { updates, hasUnread: hasUnreadUpdate(WHATS_NEW_UPDATES, updates, lastSeenId) };
}

export function WhatsNew({ lastSeenId, onSeen, suppressAutoOpen = false }: WhatsNewProps) {
  const [open, setOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { updates, hasUnread } = useWhatsNew(lastSeenId);
  const latestId = updates[0]?.id;

  const handleOpenChange = useCallback((isOpen: boolean, trigger: "auto" | "manual" = "manual") => {
    setOpen(isOpen);
    if (isOpen) phCapture("whats_new_opened", { trigger });
  }, []);

  useEffect(() => {
    if (!open || !hasUnread || !latestId) return;
    onSeen(latestId);
    markWhatsNewSeen(latestId).catch((err) => {
      console.error("Failed to mark whats-new as seen:", err);
    });
  }, [open, hasUnread, latestId, onSeen]);

  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (suppressAutoOpen || !hasUnread || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    handleOpenChange(true, "auto");
  }, [hasUnread, handleOpenChange, suppressAutoOpen]);

  useEffect(() => {
    const openManually = () => handleOpenChange(true, "manual");
    window.addEventListener(OPEN_WHATS_NEW_EVENT, openManually);
    return () => window.removeEventListener(OPEN_WHATS_NEW_EVENT, openManually);
  }, [handleOpenChange]);

  if (updates.length === 0) {
    return null;
  }

  const [latest, ...earlier] = updates.slice(0, MAX_UPDATES_SHOWN);
  const LatestIcon = updateIcon(latest);

  return (
    <Dialog open={open} onOpenChange={(next) => handleOpenChange(next)}>
      <DialogContent
        className="w-[min(32rem,calc(100vw-2rem))]"
        contentClassName="gap-0 overflow-hidden p-0 text-foreground"
        closeButtonClassName="right-1.5 top-1.5 text-muted-foreground hover:text-foreground"
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          scrollRef.current?.focus();
        }}
      >
        <div className="border-b border-border/60 px-6 pb-4 pt-5 pr-14">
          <DialogTitle className="font-display text-lg font-bold tracking-tight text-foreground">
            What&apos;s new
          </DialogTitle>
        </div>

        <div className="relative">
          <div
            ref={scrollRef}
            tabIndex={0}
            className="max-h-[min(70vh,30rem)] overflow-y-auto pb-8 outline-none scrollbar-custom scrollbar-color-primary"
          >
            <article className="px-6 pb-5 pt-5">
              <div className="flex items-start gap-3.5">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary">
                  <LatestIcon className="size-5" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="min-w-0 text-base font-semibold leading-snug text-foreground">
                      {latest.title}
                    </h3>
                    <time
                      dateTime={latest.date}
                      className="shrink-0 data-num text-[11px] uppercase text-muted-foreground"
                    >
                      {formatUpdateDate(latest.date)}
                    </time>
                  </div>
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                    {latest.description}
                  </p>
                  {latest.link && (
                    <Link
                      href={latest.link.href}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
                      onClick={() => setOpen(false)}
                    >
                      {latest.link.text} <span aria-hidden="true">→</span>
                    </Link>
                  )}
                </div>
              </div>
            </article>

            {earlier.length > 0 && (
              <>
                <div className="flex items-center gap-3 px-6 pb-1 pt-1">
                  <span className="data-num text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">
                    Earlier
                  </span>
                  <div className="h-px flex-1 bg-border/60" aria-hidden="true" />
                </div>

                {earlier.map((update) => (
                  <article
                    key={update.id}
                    className={cn(
                      "border-b border-border/40 px-6 py-4 last:border-b-0",
                      "transition-colors hover:bg-white/[0.02]"
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="min-w-0 text-sm font-medium leading-snug text-foreground">
                        {update.title}
                      </h3>
                      <time
                        dateTime={update.date}
                        className="shrink-0 data-num text-[10px] uppercase text-muted-foreground/70"
                      >
                        {formatUpdateDate(update.date)}
                      </time>
                    </div>
                    <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">
                      {update.description}
                    </p>
                    {update.link && (
                      <Link
                        href={update.link.href}
                        className="mt-1.5 inline-flex items-center gap-1 text-[13px] font-medium text-primary/90 transition-colors hover:text-primary"
                        onClick={() => setOpen(false)}
                      >
                        {update.link.text} <span aria-hidden="true">→</span>
                      </Link>
                    )}
                  </article>
                ))}
              </>
            )}
          </div>

          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-optimizer-bg to-transparent"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
